import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
  Box,
  Typography,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Badge,
} from '@mui/material';
import {
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  Description as LoggerIcon,
  SettingsInputAntenna as SocketIcon,
  Code as CodeIcon,
  Http as ApiIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import { useTheme as useMuiTheme } from '@mui/material/styles';
import { Logger } from './components/Logger';
import { SocketManagement } from './components/SocketManagement';
import { ComponentsTest } from './components/ComponentsTest';
import { ApiRequests } from './components/ApiRequests';
import { DEBUG_CONFIG } from './config';
import { NotificationProvider, useNotification } from './context/NotificationContext';
import { store as apiRequestStore } from './utils/apiRequestStore';
import { loggerStore } from './utils/loggerStore';
import { miaChatV2SocketService } from 'app/NewAIAvatarLayout/features/MiaChat/socketService/miaChatSocketService';

interface DebugPanelMAIQProps {
  open: boolean;
  onClose: () => void;
}

const DebugPanelMAIQContent: React.FC<DebugPanelMAIQProps> = ({ open, onClose }) => {
  const theme = useMuiTheme();
  const { getUnseenCount, markAsSeen } = useNotification();
  const [expandedSection, setExpandedSection] = useState<string | null>(null);
  const [currentTime, setCurrentTime] = useState(new Date());
  
  // Draggable state
  const panelRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState({ x: window.innerWidth - 600, y: 50 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  
  // Resizable state
  const [size, setSize] = useState({ width: 600, height: 700 });
  const [isResizing, setIsResizing] = useState(false);
  const [resizeStart, setResizeStart] = useState({ x: 0, y: 0, width: 0, height: 0 });
  const resizeHandleRef = useRef<HTMLDivElement>(null);

  // Load saved position and size
  useEffect(() => {
    if (open) {
      const savedPosition = localStorage.getItem('debugPanelPosition');
      const savedSize = localStorage.getItem('debugPanelSize');
      if (savedPosition) {
        try {
          setPosition(JSON.parse(savedPosition));
        } catch (e) {
          // Ignore
        }
      }
      if (savedSize) {
        try {
          setSize(JSON.parse(savedSize));
        } catch (e) {
          // Ignore
        }
      }
    }
  }, [open]);

  // Save position and size
  useEffect(() => {
    if (open) {
      localStorage.setItem('debugPanelPosition', JSON.stringify(position));
      localStorage.setItem('debugPanelSize', JSON.stringify(size));
    }
  }, [position, size, open]);

  // Drag handlers
  const handleDragStart = (e: React.MouseEvent) => {
    if (!headerRef.current) return;
    const rect = headerRef.current.getBoundingClientRect();
    setDragOffset({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    });
    setIsDragging(true);
  };

  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e: MouseEvent) => {
      const newX = e.clientX - dragOffset.x;
      const newY = e.clientY - dragOffset.y;
      
      // Keep panel within viewport
      const maxX = window.innerWidth - size.width;
      const maxY = window.innerHeight - 50; // At least 50px visible
      
      setPosition({
        x: Math.max(0, Math.min(newX, maxX)),
        y: Math.max(0, Math.min(newY, maxY)),
      });
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, dragOffset, size]);

  // Resize handlers
  const handleResizeStart = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsResizing(true);
    setResizeStart({
      x: e.clientX,
      y: e.clientY,
      width: size.width,
      height: size.height,
    });
  };

  useEffect(() => {
    if (!isResizing) return;

    const handleMouseMove = (e: MouseEvent) => {
      const deltaX = e.clientX - resizeStart.x;
      const deltaY = e.clientY - resizeStart.y;
      
      const newWidth = Math.max(400, Math.min(resizeStart.width + deltaX, window.innerWidth - position.x));
      const newHeight = Math.max(300, Math.min(resizeStart.height + deltaY, window.innerHeight - position.y));
      
      setSize({ width: newWidth, height: newHeight });
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing, resizeStart, position]);

  // Setup API request interception when panel opens
  useEffect(() => {
    if (open && DEBUG_CONFIG.enabled) {
      import('./utils/apiRequestStore').then(({ store }) => {
        store.setupInterception();
      });
    }
  }, [open]);

  // Update time every minute
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  // Remember last expanded section
  useEffect(() => {
    if (expandedSection) {
      sessionStorage.setItem('debugPanelExpandedSection', expandedSection);
    }
  }, [expandedSection]);

  const handleAccordionChange = (section: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    if (isExpanded) {
      setExpandedSection(section);
      // Mark as seen when expanded
      markAsSeen(section);
    } else {
      setExpandedSection(null);
    }
  };

  const getEnvironmentInfo = () => {
    const env = process.env.NODE_ENV || 'development';
    const isDev = env === 'development';
    return {
      env,
      color: isDev ? '#4caf50' : '#ff9800',
      label: isDev ? 'Development 🟢' : 'Production 🟠',
    };
  };

  const envInfo = getEnvironmentInfo();

  const handleDownloadDebugReport = () => {
    try {
      // Collect all debug data
      const apiRequests = apiRequestStore.getRequests();
      const logs = loggerStore.getLogs();
      
      // Get socket events
      let socketEvents: any[] = [];
      try {
        // Try to get socket service instance
        const defaultSocketUrl = process.env.REACT_APP_WS_URL || '';
        if (defaultSocketUrl) {
          const socketService = miaChatV2SocketService({
            url: defaultSocketUrl,
            autoConnect: false,
          });
          if (socketService && typeof socketService.getEventHistory === 'function') {
            socketEvents = socketService.getEventHistory() || [];
          }
        }
      } catch (error) {
        console.error('Error getting socket events:', error);
      }

      // Format timestamps for all entries
      const debugReport = {
        metadata: {
          generatedAt: new Date().toISOString(),
          environment: process.env.NODE_ENV || 'development',
          userAgent: navigator.userAgent,
          url: window.location.href,
        },
        apiRequests: apiRequests.map(req => ({
          ...req,
          timestampISO: new Date(req.timestamp).toISOString(),
          timestampFormatted: new Date(req.timestamp).toLocaleString(),
        })),
        socketEvents: socketEvents.map(evt => ({
          ...evt,
          timestampISO: new Date(evt.timestamp).toISOString(),
          timestampFormatted: new Date(evt.timestamp).toLocaleString(),
        })),
        logs: logs.map(log => ({
          ...log,
          timestampISO: new Date(log.timestamp).toISOString(),
          timestampFormatted: new Date(log.timestamp).toLocaleString(),
        })),
      };

      // Create JSON blob and download directly
      const jsonString = JSON.stringify(debugReport, null, 2);
      const blob = new Blob([jsonString], { type: 'application/json' });
      const blobUrl = URL.createObjectURL(blob);
      
      // Create download link and trigger download
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `debug-report-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up blob URL after a delay
      setTimeout(() => {
        URL.revokeObjectURL(blobUrl);
      }, 100);
    } catch (error) {
      console.error('Error generating debug report:', error);
      alert('Error generating debug report. Please check console for details.');
    }
  };

  if (!DEBUG_CONFIG.enabled || !open) {
    return null;
  }

  const accordionSections = [
    {
      id: 'socket',
      title: 'Socket Management',
      icon: <SocketIcon />,
      component: <SocketManagement />,
    },
    {
      id: 'api-requests',
      title: 'API Requests',
      icon: <ApiIcon />,
      component: <ApiRequests />,
    },
    {
      id: 'components-test',
      title: 'Components Test',
      icon: <CodeIcon />,
      component: <ComponentsTest />,
    },
    {
      id: 'logger',
      title: 'Logger',
      icon: <LoggerIcon />,
      component: <Logger />,
    },
  ];

  const panelContent = (
    <Box
      ref={panelRef}
      sx={{
        position: 'fixed',
        left: `${position.x}px`,
        top: `${position.y}px`,
        width: `${size.width}px`,
        height: `${size.height}px`,
        bgcolor: theme.palette.debug?.panel || theme.palette.background.paper,
        boxShadow: theme.shadows[8],
        borderRadius: 1,
        display: 'flex',
        flexDirection: 'column',
        zIndex: 1300,
        overflow: 'hidden',
        border: '1px solid',
        borderColor: theme.palette.debug?.border || theme.palette.divider,
      }}
    >
      {/* Header - Draggable */}
      <Box
        ref={headerRef}
        onMouseDown={handleDragStart}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 2,
          borderBottom: '1px solid',
          borderColor: theme.palette.divider,
          backgroundColor: theme.palette.primary.main,
          color: theme.palette.primary.contrastText,
          cursor: isDragging ? 'grabbing' : 'grab',
          userSelect: 'none',
          flexShrink: 0,
        }}
      >
        <Typography variant="h6" component="div" sx={{ fontWeight: 600, fontSize: 18 }}>
          MyautoIQ Debug Panel
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <IconButton
            onClick={handleDownloadDebugReport}
            sx={{
              color: theme.palette.primary.contrastText,
              '&:hover': {
                backgroundColor: theme.palette.mode === 'dark' 
                  ? 'rgba(255, 255, 255, 0.1)' 
                  : 'rgba(0, 0, 0, 0.05)',
              },
            }}
            title="Download Debug Report"
          >
            <DownloadIcon />
          </IconButton>
          <IconButton
            onClick={onClose}
            sx={{
              color: theme.palette.primary.contrastText,
              '&:hover': {
                backgroundColor: theme.palette.mode === 'dark' 
                  ? 'rgba(255, 255, 255, 0.1)' 
                  : 'rgba(0, 0, 0, 0.05)',
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </Box>

        {/* Content */}
        <Box
          sx={{
            flex: 1,
            overflow: 'auto',
            p: 2,
            bgcolor: theme.palette.debug?.panel || theme.palette.background.paper,
          }}
        >
          {accordionSections.map((section) => (
            <Accordion
              key={section.id}
              expanded={expandedSection === section.id}
              onChange={handleAccordionChange(section.id)}
              sx={{
                mb: 0.5,
                bgcolor: theme.palette.mode === 'dark' 
                  ? theme.palette.secondary.main 
                  : theme.palette.background.paper,
                '&:before': {
                  display: 'none',
                },
                boxShadow: 'none',
                border: '1px solid',
                borderColor: theme.palette.debug?.border || theme.palette.divider,
                borderRadius: 1,
                '&.Mui-expanded': {
                  margin: '0 0 8px 0',
                },
                '&:hover': {
                  bgcolor: theme.palette.action.hover,
                },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon sx={{ color: theme.palette.text.secondary }} />}
                sx={{
                  minHeight: 48,
                  '&.Mui-expanded': {
                    minHeight: 48,
                  },
                  px: 2,
                  '&:hover': {
                    bgcolor: theme.palette.action.hover,
                  },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                  <Box
                    sx={{
                      color: theme.palette.debug?.accent || theme.palette.primary.main,
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    {section.icon}
                  </Box>
                  {['socket', 'api-requests', 'logger'].includes(section.id) ? (
                    <Badge
                      badgeContent={getUnseenCount(section.id)}
                      color="error"
                      sx={{
                        '& .MuiBadge-badge': {
                          minWidth: 18,
                          height: 18,
                          fontSize: '0.65rem',
                          fontWeight: 600,
                          display: getUnseenCount(section.id) > 0 ? 'flex' : 'none',
                        },
                      }}
                    >
                      <Typography 
                        variant="body1" 
                        sx={{ 
                          fontWeight: 500, 
                          fontSize: 14,
                          color: theme.palette.text.primary,
                        }}
                      >
                        {section.title}
                      </Typography>
                    </Badge>
                  ) : (
                    <Typography 
                      variant="body1" 
                      sx={{ 
                        fontWeight: 500, 
                        fontSize: 14,
                        color: theme.palette.text.primary,
                      }}
                    >
                      {section.title}
                    </Typography>
                  )}
                </Box>
              </AccordionSummary>
              <AccordionDetails 
                sx={{ 
                  px: 2, 
                  pb: 2,
                  bgcolor: theme.palette.debug?.panel || theme.palette.background.paper,
                  borderTop: `1px solid ${theme.palette.debug?.border || theme.palette.divider}`,
                }}
              >
                {section.component}
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>

      {/* Footer */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 2,
          borderTop: '1px solid',
          borderColor: theme.palette.debug?.border || theme.palette.divider,
          backgroundColor: theme.palette.debug?.panel || theme.palette.background.paper,
          flexShrink: 0,
        }}
      >
        <Chip
          label={envInfo.label}
          size="small"
          sx={{
            bgcolor: envInfo.color,
            color: theme.palette.common.white,
            fontWeight: 500,
          }}
        />
        <Typography 
          variant="caption" 
          sx={{ 
            fontFamily: 'monospace',
            color: theme.palette.text.secondary,
          }}
        >
          {currentTime.toLocaleTimeString()}
        </Typography>
      </Box>

      {/* Resize Handle */}
      <Box
        ref={resizeHandleRef}
        onMouseDown={handleResizeStart}
        sx={{
          position: 'absolute',
          bottom: 0,
          right: 0,
          width: 20,
          height: 20,
          cursor: 'nwse-resize',
          bgcolor: 'transparent',
          '&:hover': {
            bgcolor: theme.palette.mode === 'dark' 
              ? 'rgba(255, 255, 255, 0.1)' 
              : 'rgba(0, 0, 0, 0.05)',
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            bottom: 2,
            right: 2,
            width: 0,
            height: 0,
            borderStyle: 'solid',
            borderWidth: '0 0 12px 12px',
            borderColor: `transparent transparent ${theme.palette.debug?.accent || theme.palette.text.secondary} transparent`,
          },
        }}
      />
    </Box>
  );

  return createPortal(panelContent, document.body);
};

export const DebugPanelMAIQ: React.FC<DebugPanelMAIQProps> = (props) => {
  return (
    <NotificationProvider>
      <DebugPanelMAIQContent {...props} />
    </NotificationProvider>
  );
};

export default DebugPanelMAIQ;

